/**
 * File-related utility functions for the file upload component
 * @module utils
 */

/**
 * Extension groups for file categorization
 */
export const EXTENSION_GROUPS = {
  IMAGES: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
  DOCUMENTS: ['pdf', 'doc', 'docx'],
  SPREADSHEETS: ['xls', 'xlsx'],
  PRESENTATIONS: ['ppt', 'pptx']
};

/**
 * Color mapping for different file types
 */
export const FILE_TYPE_COLORS = {
  default: '#D99F00',
  pdf: '#E03B24',
  image: '#D99F00',
  doc: '#254FE2',
  driver_license: '#E03B24',
  voided_check: '#D99F00'
};

/**
 * Get the appropriate icon based on file extension
 * @param {string} fileName - Name of the file
 * @returns {string} Icon component name
 */
export const getFileIcon = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();
  
  if (EXTENSION_GROUPS.IMAGES.includes(extension)) {
    return 'ImageIcon';
  } else if (extension === 'pdf') {
    return 'PDFIcon';
  } else if (EXTENSION_GROUPS.DOCUMENTS.includes(extension)) {
    return 'DocIcon';
  } else if (EXTENSION_GROUPS.SPREADSHEETS.includes(extension)) {
    return 'ExcelIcon';
  }
  
  return 'FileIcon';
};

/**
 * Get color for file icon based on file extension and/or document type
 * @param {string} fileName - Name of the file
 * @param {string} [documentType] - Optional document type that may override the extension-based color
 * @returns {Object} Object containing color information
 */
export const getFileTypeInfo = (fileName, documentType) => {
  const extension = fileName.split('.').pop().toLowerCase();
  
  let color = FILE_TYPE_COLORS.default;

  // Set color based on file extension
  if (['pdf'].includes(extension)) {
    color = FILE_TYPE_COLORS.pdf;
  } else if (EXTENSION_GROUPS.IMAGES.includes(extension)) {
    color = FILE_TYPE_COLORS.image;
  } else if (['doc', 'docx'].includes(extension)) {
    color = FILE_TYPE_COLORS.doc;
  }

  // Override color based on selected type if needed
  if (documentType === 'driver_license') {
    color = FILE_TYPE_COLORS.driver_license;
  } else if (documentType === 'voided_check') {
    color = FILE_TYPE_COLORS.voided_check;
  }

  return { color };
};

/**
 * Format file size to human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}; 


export const FileIconColorMapping = {
  'png': '#D99F00',
  'jpg': '#D99F00',
  'pdf': '#E03B24',
  'doc': '#4C70EF',
  'docx': '#4C70EF',
  'xls': '#31ad7e',
  'xlsx': '#31ad7e',
}